/**
 * Home — GSF Arbitral AI Vitrine
 * Design: Néo-institutionnalisme africain premium. Marine #0D1B2A / Or métallique #C9A84C.
 * Typographie: Cormorant Garamond (titres) + Inter (corps).
 * Source de vérité: Règlement d'arbitrage GSF Arbitral AI (en vigueur 2 avril 2026).
 */
import { useEffect, useRef, useState } from "react";
import {
  Scale, Brain, Shield, FileText, Clock, Users, ChevronRight,
  CheckCircle, Lock, Eye, Database, Globe, Gavel, BookOpen,
  Mail, ArrowRight, Zap, Award, BarChart3, MessageSquare,
  Timer, UserCheck, AlertTriangle, TrendingUp, Cpu, Fingerprint
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FunnelChart from "@/components/stats/FunnelChart";
import { PremiumEmblem } from "@/components/PremiumEmblem";

// ─── Assets CDN ────────────────────────────────────────────────────────────
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663353208319/eq3rmiRjKcGKGep9RNTgN7/hero-gsf-arbitral_136901f0.jpg";
const HITL_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663353208319/eq3rmiRjKcGKGep9RNTgN7/ia-hitl-gsf_c0d53c0f.jpg";
const OHADA_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663353208319/eq3rmiRjKcGKGep9RNTgN7/ohada-compliance-gsf_23a1d259.jpg";
const SECURITY_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663353208319/eq3rmiRjKcGKGep9RNTgN7/security-gsf_1fc3dd1a.jpg";

// ─── Palette GSF Arbitral AI ────────────────────────────────────────────────
const MARINE      = "oklch(0.175 0.038 243)";
const MARINE_LIGHT = "oklch(0.215 0.038 243)";
const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD        = "oklch(0.695 0.105 68)";
const GOLD_LIGHT  = "oklch(0.78 0.09 68)";
const GOLD_MUTED  = "oklch(0.58 0.085 68)";

// ─── Hook: animate on scroll ────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Animated counter ───────────────────────────────────────────────────────
function Counter({ target, suffix = "" }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const { ref, visible } = useInView(0.5);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 20);
    return () => clearInterval(timer);
  }, [visible, target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── Section header ─────────────────────────────────────────────────────────
function SectionHeader({ badge, title, subtitle, dark }: {
  badge: string; title: string; subtitle?: string; dark: boolean;
}) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className="max-w-2xl"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}
    >
      <div className="gsf-badge mb-4">{badge}</div>
      <h2
        className="font-bold leading-tight mb-4"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "clamp(1.9rem, 4vw, 3.2rem)",
          color: dark ? "oklch(1 0 0)" : MARINE,
          lineHeight: 1.08,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className="text-base leading-relaxed"
          style={{ color: dark ? "oklch(0.68 0.01 243)" : "oklch(0.48 0.01 243)", fontFamily: "'Inter', sans-serif", maxWidth: "560px" }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── Composant carte animée ─────────────────────────────────────────────────
function AnimCard({ children, delay = 0, dark = false }: { children: React.ReactNode; delay?: number; dark?: boolean }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Page principale ────────────────────────────────────────────────────────
export default function Home() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const analyticsFunnelData = [
    { stage: "Saisines reçues", dossiers: 120 },
    { stage: "Saisines validées", dossiers: 92 },
    { stage: "Audiences tenues", dossiers: 61 },
    { stage: "Sentences rendues", dossiers: 44 },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormLoading(true);
    try {
      const res = await fetch(`/api/v1/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom: contactForm.name, email: contactForm.email, message: contactForm.message }),
      });
      if (!res.ok) throw new Error("server_error");
      setFormSent(true);
    } catch {
      setFormError("Une erreur est survenue. Veuillez réessayer ou nous écrire à contact@gsfarbitral.ai.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(1 0 0)" }}>
      <Navbar />

      {/* ═══════════════════════════════════════════════════
          SECTION 1 — HERO
      ═══════════════════════════════════════════════════ */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: MARINE_DEEP }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})`, opacity: 0.30 }}
        />
        {/* Gradient overlay — left dark, right transparent */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(110deg, oklch(0.115 0.038 243 / 0.97) 40%, oklch(0.115 0.038 243 / 0.45) 100%)" }}
        />
        {/* Subtle gold line top */}
        <div className="absolute top-0 left-0 right-0" style={{ height: "2px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />

        <div className="container relative z-10 pt-28 pb-20">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_auto] gap-16 items-center">
            <div className="max-w-2xl">
            {/* Badge */}
            <div className="gsf-badge mb-6 animate-fade-up" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Zap size={10} />
              Règlement en vigueur — 2 avril 2026
            </div>

            {/* Title */}
            <h1
              className="font-bold leading-tight mb-6 animate-fade-up delay-100"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2.8rem, 6.5vw, 5rem)",
                color: "oklch(1 0 0)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
              }}
            >
              L'Arbitrage OHADA
              <br />
              <span style={{ color: GOLD }}>Accéléré par l'IA</span>
            </h1>

            {/* Subtitle */}
            <p
              className="text-lg leading-relaxed mb-10 animate-fade-up delay-200"
              style={{ color: "oklch(0.78 0.01 243)", maxWidth: "540px", fontFamily: "'Inter', sans-serif", fontSize: "1.05rem" }}
            >
              GSF Arbitral AI résout les litiges commerciaux de l'espace OHADA en moins de 90 jours grâce à un moteur d'intelligence artificielle privé et un workflow «&nbsp;Human in the Loop&nbsp;» certifié.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 animate-fade-up delay-300">
              <a
                href="#contact"
                className="gsf-btn-primary"
                onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              >
                Demander une démonstration
                <ArrowRight size={15} />
              </a>
              <a
                href="#plateforme"
                className="gsf-btn-outline"
                onClick={(e) => { e.preventDefault(); document.querySelector("#plateforme")?.scrollIntoView({ behavior: "smooth" }); }}
              >
                Découvrir la plateforme
              </a>
            </div>

            {/* Stats — source: Règlement GSF Arbitral Art. 1 & Annexe A */}
            <div className="flex flex-wrap gap-10 mt-16 animate-fade-up delay-400">
              {[
                { value: 90,  suffix: "j",    label: "Délai garanti" },
                { value: 48,  suffix: "h",    label: "Arbitre urgence" },
                { value: 17,  suffix: " pays", label: "Espace OHADA" },
                { value: 100, suffix: "%",    label: "Confidentiel" },
              ].map((stat) => (
                <div key={stat.label} className="border-l pl-5" style={{ borderColor: "oklch(0.695 0.105 68 / 0.35)" }}>
                  <div
                    className="text-3xl font-bold"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD, lineHeight: 1 }}
                  >
                    <Counter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs uppercase tracking-widest mt-1.5" style={{ color: "oklch(0.55 0.01 243)", letterSpacing: "0.1em" }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
            </div>

            {/* Emblème institutionnel animé — visible en desktop uniquement */}
            <div className="hidden lg:flex items-center justify-center animate-fade-up delay-200">
              <PremiumEmblem size={260} />
            </div>
          </div>
        </div>

        {/* Bottom gold line */}
        <div className="absolute bottom-0 left-0 right-0 gsf-gold-line" />
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 2 — PRÉSENTATION DE LA PLATEFORME
      ═══════════════════════════════════════════════════ */}
      <section id="plateforme" className="py-28" style={{ background: "oklch(1 0 0)" }}>
        <div className="container">
          <SectionHeader
            badge="La Plateforme"
            title="Souveraineté Cognitive Opérationnelle™"
            dark={false}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            {[
              {
                icon: <Brain size={22} />,
                title: "Moteur IA Privé",
                desc: "Analyse automatisée des sentences arbitrales via le moteur RAG OHADA. Détection des irrégularités, conformité AUA 2017, génération de rapports d'audit certifiés.",
                delay: 0,
              },
              {
                icon: <UserCheck size={22} />,
                title: "Human in the Loop",
                desc: "5 niveaux de supervision humaine. L'arbitre conserve le contrôle total. L'IA assiste et documente — la décision finale reste humaine et juridiquement souveraine (Art. 37).",
                delay: 100,
              },
              {
                icon: <Globe size={22} />,
                title: "17 États OHADA",
                desc: "Couverture complète de l'espace OHADA. Procédures institutionnelles et ad hoc. Exequatur cible en 15 jours ouvrables. Langues : français et anglais.",
                delay: 200,
              },
            ].map((card) => (
              <AnimCard key={card.title} delay={card.delay}>
                <div className="gsf-card-light p-8 rounded-sm h-full">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-sm mb-5"
                    style={{ background: "oklch(0.175 0.038 243 / 0.07)", color: MARINE }}
                  >
                    {card.icon}
                  </div>
                  <h3
                    className="text-xl font-semibold mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                    {card.desc}
                  </p>
                </div>
              </AnimCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 3 — DÉLAIS FAST-TRACK (NOUVEAU)
      ═══════════════════════════════════════════════════ */}
      <section className="py-20 relative overflow-hidden" style={{ background: MARINE }}>
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 70% 50%, oklch(0.695 0.105 68 / 0.06) 0%, transparent 60%)" }} />
        <div className="container relative z-10">
          <SectionHeader
            badge="Fast-Track OHADA"
            title="Des Délais Garantis par le Règlement"
            subtitle="Chaque étape de la procédure est encadrée par des délais contractuels précis, inscrits dans le Règlement d'arbitrage GSF Arbitral AI (en vigueur 2 avril 2026)."
            dark={true}
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-14">
            {[
              { icon: <Zap size={20} />, delay_val: "48h", label: "Arbitre d'urgence nommé", note: "Art. 10 — Urgence" },
              { icon: <Timer size={20} />, delay_val: "15j", label: "Ordonnance d'urgence rendue", note: "Art. 10 — Urgence" },
              { icon: <FileText size={20} />, delay_val: "30j", label: "Acte de mission signé", note: "Art. 18 — Procédure" },
              { icon: <Scale size={20} />, delay_val: "90j", label: "Sentence arbitrale rendue", note: "Art. 1 — Garantie" },
            ].map((item, i) => (
              <AnimCard key={item.label} delay={i * 80}>
                <div
                  className="p-6 rounded-sm text-center"
                  style={{ background: MARINE_LIGHT, border: "1px solid oklch(0.695 0.105 68 / 0.18)" }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-sm mx-auto mb-4"
                    style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}
                  >
                    {item.icon}
                  </div>
                  <div
                    className="text-4xl font-bold mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD, lineHeight: 1 }}
                  >
                    {item.delay_val}
                  </div>
                  <p className="text-sm font-medium mb-2" style={{ color: "oklch(0.88 0.01 243)" }}>
                    {item.label}
                  </p>
                  <p className="text-xs" style={{ color: "oklch(0.55 0.01 243)" }}>
                    {item.note}
                  </p>
                </div>
              </AnimCard>
            ))}
          </div>

          {/* HITL image */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimCard>
              <img
                src={HITL_IMG}
                alt="Workflow Human in the Loop GSF Arbitral AI"
                className="w-full rounded-sm"
                style={{ border: "1px solid oklch(0.695 0.105 68 / 0.2)", maxHeight: "380px", objectFit: "cover" }}
              />
            </AnimCard>
            <AnimCard delay={150}>
              <div className="space-y-5">
                <h3
                  className="text-2xl font-bold"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                >
                  5 Niveaux de Supervision Humaine
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.68 0.01 243)" }}>
                  Conformément à la Charte Éthique IA (Annexe C du Règlement), chaque décision critique est soumise à validation humaine. Au-delà de 100 M FCFA, la supervision HITL est obligatoire.
                </p>
                {[
                  "Niveau 1 — Pré-analyse IA (automatique)",
                  "Niveau 2 — Validation Greffier",
                  "Niveau 3 — Contrôle Arbitre",
                  "Niveau 4 — Supervision HITL (> 100M FCFA)",
                  "Niveau 5 — Délibération exclusivement humaine (Art. 37)",
                ].map((lvl, i) => (
                  <div key={lvl} className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-sm flex-shrink-0 text-xs font-bold"
                      style={{ background: i < 2 ? "oklch(0.695 0.105 68 / 0.2)" : GOLD, color: i < 2 ? GOLD : MARINE, fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {i + 1}
                    </div>
                    <span className="text-sm" style={{ color: i === 4 ? GOLD_LIGHT : "oklch(0.72 0.01 243)" }}>
                      {lvl}
                    </span>
                  </div>
                ))}
              </div>
            </AnimCard>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 4 — FONCTIONNALITÉS
      ═══════════════════════════════════════════════════ */}
      <section id="fonctionnalites" className="py-28" style={{ background: "oklch(0.97 0.005 240)" }}>
        <div className="container">
          <SectionHeader
            badge="Fonctionnalités"
            title="Une Plateforme Complète pour l'Arbitrage Moderne"
            subtitle="De la saisine à l'archivage de la sentence, chaque étape de la procédure est gérée, tracée et sécurisée sur une seule plateforme."
            dark={false}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-16">
            {[
              { icon: <FileText size={20} />, title: "Dépôt de Dossier", desc: "Saisine en ligne, numérotation GSF automatique, validation documentaire instantanée.", delay: 0 },
              { icon: <Brain size={20} />, title: "Audit IA Automatique", desc: "Analyse de la sentence par le moteur RAG OHADA — conformité AUA 2017, cohérence juridique, détection d'anomalies.", delay: 60 },
              { icon: <Users size={20} />, title: "Gestion des Arbitres", desc: "Sélection, affectation, suivi des disponibilités et gestion des conflits d'intérêts.", delay: 120 },
              { icon: <MessageSquare size={20} />, title: "Messagerie Sécurisée", desc: "Communications chiffrées entre parties, arbitres et secrétariat. Journal d'audit complet.", delay: 180 },
              { icon: <Clock size={20} />, title: "Calendrier Procédural", desc: "Planification automatique des audiences, délais OHADA respectés, rappels automatiques.", delay: 0 },
              { icon: <FileText size={20} />, title: "Modèles Juridiques", desc: "Bibliothèque de modèles : actes de mission, sentences, ordonnances de procédure.", delay: 60 },
              { icon: <BarChart3 size={20} />, title: "Tableau de Bord", desc: "Suivi en temps réel des dossiers, indicateurs de performance, rapports d'activité.", delay: 120 },
              { icon: <Database size={20} />, title: "Archivage Sécurisé 30 ans", desc: "Conservation longue durée certifiée des sentences et pièces. Conformité RGPD et droit OHADA.", delay: 180 },
            ].map((feat) => (
              <AnimCard key={feat.title} delay={feat.delay}>
                <div className="gsf-card-light p-6 rounded-sm h-full">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-sm mb-4"
                    style={{ background: "oklch(0.695 0.105 68 / 0.10)", color: "oklch(0.58 0.085 68)" }}
                  >
                    {feat.icon}
                  </div>
                  <h4
                    className="font-semibold text-base mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                  >
                    {feat.title}
                  </h4>
                  <p className="text-xs leading-relaxed" style={{ color: "oklch(0.50 0.01 243)" }}>
                    {feat.desc}
                  </p>
                </div>
              </AnimCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION — STATISTIQUES
      ═══════════════════════════════════════════════════ */}
      <section id="statistiques" className="py-24" style={{ background: "oklch(1 0 0)" }}>
        <div className="container">
          <SectionHeader
            badge="Statistiques"
            title="Entonnoir de traitement des dossiers"
            subtitle="Visualisation consolidée des étapes clés du pipeline arbitral pour le suivi opérationnel."
            dark={false}
          />
          <div className="mt-10 gsf-card-light p-6 rounded-sm">
            <FunnelChart data={analyticsFunnelData} />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 5 — CONFORMITÉ OHADA
      ═══════════════════════════════════════════════════ */}
      <section id="ohada" className="py-28 relative overflow-hidden" style={{ background: MARINE }}>
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at 30% 70%, oklch(0.695 0.105 68 / 0.05) 0%, transparent 55%)" }} />
        <div className="container relative z-10">
          <SectionHeader
            badge="Conformité OHADA"
            title="Ancré dans le Droit Africain des Affaires"
            subtitle="La plateforme est conçue en stricte conformité avec l'Acte Uniforme relatif au droit de l'arbitrage (AUA 2017) et le Règlement d'arbitrage GSF Arbitral AI (en vigueur 2 avril 2026)."
            dark={true}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16 items-start">
            <div className="space-y-4">
              {[
                {
                  icon: <Gavel size={18} />,
                  title: "Acte Uniforme sur l'Arbitrage (AUA 2017)",
                  desc: "Conformité totale avec l'AUA OHADA. Toutes les phases procédurales, l'exécution des sentences et les voies de recours sont organisées selon ce texte fondateur.",
                },
                {
                  icon: <Award size={18} />,
                  title: "Règlement GSF Arbitral AI — 2 avril 2026",
                  desc: "Règlement propre à la plateforme, complémentaire à l'AUA. Il régit la saisine dématérialisée, les délais Fast-Track, les frais et le protocole HITL.",
                },
                {
                  icon: <Scale size={18} />,
                  title: "Indépendance & Impartialité",
                  desc: "Mécanismes automatisés de détection des conflits d'intérêts. Séparation stricte des rôles entre parties, arbitres et secrétariat.",
                },
                {
                  icon: <Lock size={18} />,
                  title: "Confidentialité Absolue",
                  desc: "Toutes les procédures sont strictement confidentielles. Accès compartimenté par dossier. Aucune donnée partagée entre instances.",
                },
                {
                  icon: <BookOpen size={18} />,
                  title: "Médiation OHADA Préalable",
                  desc: "Module de médiation intégré, conforme à l'Acte Uniforme sur la Médiation OHADA, proposé systématiquement avant toute procédure arbitrale.",
                },
              ].map((item, i) => (
                <AnimCard key={item.title} delay={i * 80}>
                  <div
                    className="flex gap-4 p-5 rounded-sm"
                    style={{ background: MARINE_LIGHT, border: "1px solid oklch(0.695 0.105 68 / 0.12)" }}
                  >
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0 mt-0.5"
                      style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h4
                        className="font-semibold text-base mb-1"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                      >
                        {item.title}
                      </h4>
                      <p className="text-sm leading-relaxed" style={{ color: "oklch(0.65 0.01 243)" }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </AnimCard>
              ))}
            </div>

            <div className="space-y-6">
              {/* Image OHADA */}
              <AnimCard>
                <img
                  src={OHADA_IMG}
                  alt="Conformité OHADA GSF Arbitral AI"
                  className="w-full rounded-sm"
                  style={{ border: "1px solid oklch(0.695 0.105 68 / 0.2)", maxHeight: "260px", objectFit: "cover" }}
                />
              </AnimCard>

              {/* Secteurs */}
              <AnimCard delay={100}>
                <div
                  className="p-7 rounded-sm"
                  style={{ background: MARINE_LIGHT, border: "1px solid oklch(0.695 0.105 68 / 0.2)" }}
                >
                  <h3
                    className="text-lg font-semibold mb-5"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}
                  >
                    Secteurs Couverts
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                    {[
                      { label: "Contrats commerciaux" },
                      { label: "BTP & Travaux Publics" },
                      { label: "Énergie & Mines", tag: "Pré-sentence économique" },
                      { label: "Finance & Banque", tag: "Crypto & smart contracts" },
                      { label: "Télécommunications" },
                      { label: "Investissements transfrontaliers" },
                      { label: "Propriété intellectuelle", tag: "Litiges IA générative" },
                      { label: "Droit des sociétés" },
                    ].map((sector) => (
                      <div key={sector.label} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={11} style={{ color: GOLD, flexShrink: 0 }} />
                          <span className="text-xs" style={{ color: "oklch(0.72 0.01 243)" }}>
                            {sector.label}
                          </span>
                        </div>
                        {sector.tag && (
                          <span
                            className="text-[10px] uppercase font-medium ml-[19px] pl-2 py-0.5"
                            style={{
                              color: GOLD_LIGHT,
                              letterSpacing: "0.14em",
                              borderLeft: "1.5px solid oklch(0.695 0.105 68 / 0.45)",
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {sector.tag}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </AnimCard>

              {/* Fonctionnalités Exclusives */}
              <AnimCard delay={150}>
                <div
                  className="p-7 rounded-sm relative overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, oklch(0.215 0.038 243) 0%, oklch(0.175 0.038 243) 100%)",
                    border: "1px solid oklch(0.695 0.105 68 / 0.32)",
                    boxShadow: "0 10px 30px -10px oklch(0.695 0.105 68 / 0.15)",
                  }}
                >
                  <div
                    className="absolute top-0 right-0 px-3 py-1 text-[9px] uppercase font-semibold"
                    style={{
                      background: "oklch(0.695 0.105 68 / 0.18)",
                      color: GOLD,
                      letterSpacing: "0.18em",
                      fontFamily: "'Inter', sans-serif",
                      borderLeft: "1px solid oklch(0.695 0.105 68 / 0.35)",
                      borderBottom: "1px solid oklch(0.695 0.105 68 / 0.35)",
                    }}
                  >
                    Exclusif
                  </div>
                  <h3
                    className="text-lg font-semibold mb-5"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}
                  >
                    Fonctionnalités Exclusives
                  </h3>
                  <div className="space-y-4">
                    {[
                      {
                        icon: <Fingerprint size={16} />,
                        title: "TrustLedger OHADA",
                        desc: "Preuve blockchain immuable des actes procéduraux",
                      },
                      {
                        icon: <UserCheck size={16} />,
                        title: "HITL Desk",
                        desc: "Supervision humaine certifiée — Art. 37 AUA",
                      },
                      {
                        icon: <BarChart3 size={16} />,
                        title: "Observatoire des Litiges du Futur",
                        desc: "Accès premium aux tendances OHADA",
                      },
                    ].map((f) => (
                      <div key={f.title} className="flex items-start gap-3">
                        <div
                          className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0 mt-0.5"
                          style={{
                            background: "oklch(0.695 0.105 68 / 0.15)",
                            color: GOLD,
                            border: "1px solid oklch(0.695 0.105 68 / 0.3)",
                          }}
                        >
                          {f.icon}
                        </div>
                        <div>
                          <h4
                            className="text-sm font-semibold mb-0.5"
                            style={{ color: "oklch(1 0 0)", fontFamily: "'Inter', sans-serif" }}
                          >
                            {f.title}
                          </h4>
                          <p className="text-xs leading-relaxed" style={{ color: "oklch(0.65 0.01 243)" }}>
                            {f.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimCard>

              {/* Citation */}
              <AnimCard delay={200}>
                <div
                  className="p-6 rounded-sm"
                  style={{ background: "oklch(0.695 0.105 68 / 0.07)", border: "1px solid oklch(0.695 0.105 68 / 0.28)" }}
                >
                  <p
                    className="text-sm italic leading-relaxed"
                    style={{ color: GOLD_LIGHT, fontFamily: "'Cormorant Garamond', serif", fontSize: "1.05rem" }}
                  >
                    "Le droit OHADA de l'arbitrage organise les phases de la procédure, l'exécution des sentences et les voies de recours, tout en visant transparence, rapidité et efficacité."
                  </p>
                  <p className="text-xs mt-3" style={{ color: GOLD_MUTED }}>
                    — OHADA.org, Arbitration Law
                  </p>
                </div>
              </AnimCard>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 6 — SÉCURITÉ
      ═══════════════════════════════════════════════════ */}
      <section
        id="securite"
        className="py-28 relative overflow-hidden"
        style={{ background: MARINE_DEEP }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${SECURITY_IMG})`, opacity: 0.12 }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, oklch(0.115 0.038 243 / 0.9) 0%, oklch(0.115 0.038 243 / 0.75) 100%)" }} />
        <div className="container relative z-10">
          <SectionHeader
            badge="Sécurité & Confidentialité"
            title="Architecture Cloud Native Sécurisée"
            subtitle="Conforme AI Act & RGPD. Chaque donnée est chiffrée, tracée et souveraine. La technologie ne fragilise jamais la validité juridique des procédures."
            dark={true}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
            {[
              { icon: <Lock size={20} />, title: "Chiffrement de bout en bout", desc: "Toutes les communications et documents sont chiffrés en transit et au repos. Clés de chiffrement dédiées par dossier.", delay: 0 },
              { icon: <Eye size={20} />, title: "Journal d'Audit Complet", desc: "Traçabilité intégrale de chaque action : consultation, modification, téléchargement. Horodatage certifié TSA.", delay: 80 },
              { icon: <Shield size={20} />, title: "Contrôle d'Accès Granulaire", desc: "Séparation stricte des rôles : parties, arbitres, secrétariat. Accès limité au strict nécessaire par dossier.", delay: 160 },
              { icon: <Fingerprint size={20} />, title: "Souveraineté des Données", desc: "Hébergement dans l'espace juridictionnel OHADA. Aucun transfert de données hors du périmètre contractuel.", delay: 0 },
              { icon: <Cpu size={20} />, title: "Conformité RGPD & AI Act", desc: "Traitement des données personnelles conforme au RGPD. Algorithmes explicables et auditables (AI Act, Charte Annexe C).", delay: 80 },
              { icon: <TrendingUp size={20} />, title: "Disponibilité 99,9%", desc: "Infrastructure redondante, sauvegardes automatiques, reprise sur incident garantie pour les procédures en cours.", delay: 160 },
            ].map((item) => (
              <AnimCard key={item.title} delay={item.delay}>
                <div className="gsf-card-dark p-6 rounded-sm h-full">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-sm mb-4"
                    style={{ background: "oklch(0.695 0.105 68 / 0.12)", color: GOLD }}
                  >
                    {item.icon}
                  </div>
                  <h4
                    className="font-semibold text-base mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-xs leading-relaxed" style={{ color: "oklch(0.60 0.01 243)" }}>
                    {item.desc}
                  </p>
                </div>
              </AnimCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 7 — PROCESSUS
      ═══════════════════════════════════════════════════ */}
      <section className="py-28" style={{ background: "oklch(1 0 0)" }}>
        <div className="container">
          <SectionHeader
            badge="Comment ça marche"
            title="7 Étapes vers la Sentence Arbitrale"
            subtitle="Un processus clair, structuré et entièrement dématérialisé. De la saisine à l'archivage, chaque étape est guidée par la plateforme et encadrée par le Règlement."
            dark={false}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-16 items-center">
            <div className="space-y-3">
              {[
                { n: "01", title: "Dépôt de la Demande", desc: "Saisine en ligne avec numérotation GSF automatique et validation documentaire." },
                { n: "02", title: "Validation & Enregistrement", desc: "Vérification de recevabilité, notification des parties, constitution du dossier." },
                { n: "03", title: "Affectation de l'Arbitre", desc: "Sélection selon disponibilité, compétence et absence de conflit d'intérêts. Nommé en 48h pour l'urgence." },
                { n: "04", title: "Échanges Procéduraux", desc: "Mémoires, pièces, communications via la messagerie sécurisée de la plateforme." },
                { n: "05", title: "Audience Virtuelle ou Présentielle", desc: "Planification automatique, visioconférence intégrée ou salle d'audience physique." },
                { n: "06", title: "Rendu de la Sentence", desc: "Audit IA automatique (moteur RAG OHADA), validation par l'arbitre, notification aux parties." },
                { n: "07", title: "Archivage & Exécution", desc: "Conservation sécurisée 30 ans, assistance pour l'exequatur (cible : 15 jours ouvrables) et l'exécution forcée." },
              ].map((step, i) => (
                <AnimCard key={step.n} delay={i * 60}>
                  <div className="flex gap-4 items-start py-3 border-b" style={{ borderColor: "oklch(0.92 0.005 240)" }}>
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0 font-bold"
                      style={{
                        background: i < 3 ? MARINE : "oklch(0.695 0.105 68 / 0.10)",
                        color: i < 3 ? GOLD : "oklch(0.58 0.085 68)",
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "1rem",
                      }}
                    >
                      {step.n}
                    </div>
                    <div className="pt-0.5">
                      <h4
                        className="font-semibold text-base mb-0.5"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                      >
                        {step.title}
                      </h4>
                      <p className="text-xs leading-relaxed" style={{ color: "oklch(0.50 0.01 243)" }}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </AnimCard>
              ))}
            </div>

            <AnimCard delay={200}>
              <div
                className="p-8 rounded-sm"
                style={{ background: MARINE, border: "1px solid oklch(0.695 0.105 68 / 0.2)" }}
              >
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}
                >
                  Délais Fast-Track Garantis
                </h3>
                <p className="text-xs mb-6" style={{ color: "oklch(0.55 0.01 243)" }}>
                  Source : Règlement GSF Arbitral AI, en vigueur 2 avril 2026
                </p>
                <div className="space-y-4">
                  {[
                    { label: "Arbitre d'urgence nommé", val: "48 heures", art: "Art. 10" },
                    { label: "Ordonnance d'urgence", val: "15 jours", art: "Art. 10" },
                    { label: "Acte de mission", val: "30 jours", art: "Art. 18" },
                    { label: "Sentence arbitrale", val: "≤ 90 jours", art: "Art. 1" },
                    { label: "Exequatur cible", val: "15 jours ouvrables", art: "Art. 28" },
                  ].map((d) => (
                    <div key={d.label} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "oklch(0.695 0.105 68 / 0.12)" }}>
                      <div>
                        <span className="text-sm" style={{ color: "oklch(0.80 0.01 243)" }}>{d.label}</span>
                        <span className="ml-2 text-xs" style={{ color: "oklch(0.50 0.01 243)" }}>— {d.art}</span>
                      </div>
                      <span
                        className="text-sm font-semibold"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}
                      >
                        {d.val}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimCard>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 8 — RÈGLEMENT & BARÈMES
      ═══════════════════════════════════════════════════ */}
      <section id="reglement" className="py-28" style={{ background: "oklch(0.97 0.005 243)" }}>
        <div className="container">
          <SectionHeader
            badge="Règlement & Transparence Tarifaire"
            title="Cadre Procédural & Barèmes Officiels"
            subtitle="Le Règlement d'arbitrage GSF Arbitral AI (en vigueur 2 avril 2026) constitue le cadre procédural de référence. Les frais et honoraires sont transparents et prévisibles dès la saisine."
            dark={false}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            {/* Règlement GSF */}
            <AnimCard>
              <div
                className="p-8 rounded-sm h-full"
                style={{ background: MARINE, border: "1px solid oklch(0.695 0.105 68 / 0.2)" }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-sm"
                    style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}
                  >
                    <Scale size={18} />
                  </div>
                  <div>
                    <h3
                      className="text-xl font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                    >
                      Règlement GSF Arbitral AI
                    </h3>
                    <p className="text-xs" style={{ color: GOLD_MUTED }}>En vigueur depuis le 2 avril 2026</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    "Saisine dématérialisée et numérotation GSF",
                    "Délai maximum de 90 jours garanti contractuellement",
                    "Audit IA obligatoire avant rendu de sentence",
                    "Validation humaine systématique (Human in the Loop)",
                    "5 niveaux HITL — supervision obligatoire > 100M FCFA",
                    "Médiation préalable proposée systématiquement",
                    "Archivage certifié 30 ans",
                    "Délibération exclusivement humaine (Art. 37)",
                  ].map((rule) => (
                    <li key={rule} className="flex items-start gap-2">
                      <ChevronRight size={13} style={{ color: GOLD, flexShrink: 0, marginTop: "3px" }} />
                      <span className="text-sm" style={{ color: "oklch(0.70 0.01 243)" }}>{rule}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 mt-6 text-sm font-medium"
                  style={{ color: GOLD, textDecoration: "none" }}
                  onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                >
                  Demander le règlement complet
                  <ArrowRight size={14} />
                </a>
              </div>
            </AnimCard>

            {/* Barèmes officiels */}
            <AnimCard delay={120}>
              <div
                className="p-8 rounded-sm h-full"
                style={{ background: "oklch(1 0 0)", border: "1px solid oklch(0.90 0.005 240)" }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-sm"
                    style={{ background: MARINE, color: GOLD }}
                  >
                    <BarChart3 size={18} />
                  </div>
                  <div>
                    <h3
                      className="text-xl font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                    >
                      Barèmes Officiels
                    </h3>
                    <p className="text-xs" style={{ color: "oklch(0.55 0.01 243)" }}>Source : Annexe A du Règlement</p>
                  </div>
                </div>

                {/* Frais administratifs */}
                <h4 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD_MUTED, letterSpacing: "0.1em" }}>
                  Frais Administratifs
                </h4>
                <div className="space-y-2 mb-5">
                  {[
                    { tranche: "Jusqu'à 5 M FCFA", montant: "300 000 FCFA" },
                    { tranche: "5 M – 50 M FCFA", montant: "4% du montant" },
                    { tranche: "50 M – 200 M FCFA", montant: "3% du montant" },
                    { tranche: "> 200 M FCFA", montant: "Sur devis" },
                  ].map((row) => (
                    <div key={row.tranche} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: "oklch(0.93 0.005 240)" }}>
                      <span className="text-xs" style={{ color: "oklch(0.45 0.01 243)" }}>{row.tranche}</span>
                      <span className="text-xs font-semibold" style={{ color: MARINE }}>{row.montant}</span>
                    </div>
                  ))}
                </div>

                {/* Honoraires arbitre */}
                <h4 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD_MUTED, letterSpacing: "0.1em" }}>
                  Honoraires Arbitre Unique
                </h4>
                <div className="space-y-2 mb-5">
                  {[
                    { tranche: "Jusqu'à 50 M FCFA", montant: "500 000 – 2,5 M FCFA" },
                    { tranche: "50 M – 200 M FCFA", montant: "2,5 M – 6 M FCFA" },
                    { tranche: "> 200 M FCFA", montant: "Sur devis" },
                  ].map((row) => (
                    <div key={row.tranche} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: "oklch(0.93 0.005 240)" }}>
                      <span className="text-xs" style={{ color: "oklch(0.45 0.01 243)" }}>{row.tranche}</span>
                      <span className="text-xs font-semibold" style={{ color: MARINE }}>{row.montant}</span>
                    </div>
                  ))}
                </div>

                {/* Frais technologiques */}
                <h4 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD_MUTED, letterSpacing: "0.1em" }}>
                  Frais Technologiques
                </h4>
                <div className="space-y-2">
                  {[
                    { label: "Pré-analyse IA", montant: "150 000 FCFA" },
                    { label: "Transcription IA", montant: "25 000 FCFA/h" },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: "oklch(0.93 0.005 240)" }}>
                      <span className="text-xs" style={{ color: "oklch(0.45 0.01 243)" }}>{row.label}</span>
                      <span className="text-xs font-semibold" style={{ color: MARINE }}>{row.montant}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs mt-4" style={{ color: "oklch(0.55 0.01 243)" }}>
                  Pour un devis détaillé, contactez{" "}
                  <a href="mailto:contact@gsfarbitral.ai" style={{ color: GOLD_MUTED }}>
                    coontact@gsfarbitral.ai
                  </a>
                </p>
              </div>
            </AnimCard>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 9 — RESSOURCES
      ═══════════════════════════════════════════════════ */}
      <section id="ressources" className="py-28" style={{ background: MARINE }}>
        <div className="container">
          <SectionHeader
            badge="Ressources"
            title="Documentation & Références Juridiques"
            subtitle="Accédez aux textes officiels, guides pratiques et analyses pour maîtriser le cadre juridique de l'arbitrage OHADA."
            dark={true}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
            {[
              {
                category: "Textes Officiels",
                items: [
                  { title: "Acte Uniforme sur l'Arbitrage (AUA 2017)", href: "https://www.ohada.org/en/arbitration-law/" },
                  { title: "Règlement d'Arbitrage CCJA 2017", href: "https://www.ohada.org/en/ccja-arbitration-rules/" },
                  { title: "Acte Uniforme sur la Médiation", href: "https://www.ohada.org" },
                ],
              },
              {
                category: "Guides Pratiques",
                items: [
                  { title: "Réforme OHADA 2017 — Analyse HSF", href: "https://www.hsfkramer.com/notes/arbitration/2017-12/ohada-arbitration-reform-publication-of-the-new-uniform-act-on-arbitration-and-the-revised-ccja-arbitration-rules" },
                  { title: "Vue d'ensemble OHADA — ACC", href: "https://www.acc.com/resource-library/quick-overview-dispute-resolution-under-ohada-framework" },
                  { title: "Attractivité du nouvel Acte OHADA", href: "https://globallawexperts.com/the-attractiveness-of-the-new-ohada-arbitration-act/" },
                ],
              },
              {
                category: "Règlement GSF Arbitral",
                items: [
                  { title: "Demander le Règlement complet", href: "#contact" },
                  { title: "Charte Éthique IA (Annexe C)", href: "#contact" },
                  { title: "Barèmes officiels (Annexe A)", href: "#reglement" },
                ],
              },
            ].map((group) => (
              <AnimCard key={group.category}>
                <div className="gsf-card-dark p-6 rounded-sm h-full">
                  <h4
                    className="text-xs font-semibold uppercase tracking-widest mb-5"
                    style={{ color: GOLD, letterSpacing: "0.12em" }}
                  >
                    {group.category}
                  </h4>
                  <ul className="space-y-3">
                    {group.items.map((item) => (
                      <li key={item.title}>
                        <a
                          href={item.href}
                          target={item.href.startsWith("http") ? "_blank" : "_self"}
                          rel="noopener noreferrer"
                          className="flex items-start gap-2 text-sm transition-colors"
                          style={{ color: "oklch(0.72 0.01 243)", textDecoration: "none" }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "oklch(0.72 0.01 243)")}
                          onClick={item.href.startsWith("#") ? (e) => { e.preventDefault(); document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" }); } : undefined}
                        >
                          <ChevronRight size={13} style={{ flexShrink: 0, marginTop: "2px" }} />
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 10 — CONTACT
      ═══════════════════════════════════════════════════ */}
      <section id="contact" className="py-28" style={{ background: "oklch(1 0 0)" }}>
        <div className="container">
          <SectionHeader
            badge="Contact"
            title="Prêt à Moderniser votre Arbitrage ?"
            subtitle="Demandez une démonstration personnalisée ou contactez-nous pour toute question sur la plateforme, les tarifs ou l'intégration dans votre institution."
            dark={false}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mt-16">
            {/* Form */}
            <AnimCard>
              {formSent ? (
                <div
                  className="p-10 rounded-sm text-center"
                  style={{ background: "oklch(0.695 0.105 68 / 0.07)", border: "1px solid oklch(0.695 0.105 68 / 0.28)" }}
                >
                  <CheckCircle size={40} style={{ color: GOLD, margin: "0 auto 1rem" }} />
                  <h3
                    className="text-2xl font-semibold mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                  >
                    Message envoyé
                  </h3>
                  <p className="text-sm" style={{ color: "oklch(0.50 0.01 243)" }}>
                    Nous vous répondrons dans les 24 heures ouvrées.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {[
                    { label: "Nom complet", type: "text", key: "name", placeholder: "Maître Jean Dupont" },
                    { label: "Adresse email", type: "email", key: "email", placeholder: "contact@cabinet.com" },
                  ].map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium uppercase tracking-widest mb-2" style={{ color: "oklch(0.40 0.01 243)", letterSpacing: "0.08em" }}>
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        required
                        value={contactForm[field.key as keyof typeof contactForm]}
                        onChange={(e) => setContactForm({ ...contactForm, [field.key]: e.target.value })}
                        className="w-full px-4 py-3 text-sm outline-none transition-all"
                        style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.88 0.005 240)", color: MARINE, borderRadius: "2px" }}
                        onFocus={(e) => (e.target.style.borderColor = GOLD)}
                        onBlur={(e) => (e.target.style.borderColor = "oklch(0.88 0.005 240)")}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest mb-2" style={{ color: "oklch(0.40 0.01 243)", letterSpacing: "0.08em" }}>
                      Message
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full px-4 py-3 text-sm outline-none transition-all resize-none"
                      style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.88 0.005 240)", color: MARINE, borderRadius: "2px" }}
                      onFocus={(e) => (e.target.style.borderColor = GOLD)}
                      onBlur={(e) => (e.target.style.borderColor = "oklch(0.88 0.005 240)")}
                      placeholder="Décrivez votre besoin ou demandez une démonstration..."
                    />
                  </div>
                  {formError && (
                    <p className="text-sm" style={{ color: "#c0392b" }}>{formError}</p>
                  )}
                  <button type="submit" disabled={formLoading} className="gsf-btn-primary w-full justify-center" style={{ opacity: formLoading ? 0.7 : 1 }}>
                    {formLoading ? "Envoi en cours…" : "Envoyer le message"}
                    {!formLoading && <ArrowRight size={15} />}
                  </button>
                </form>
              )}
            </AnimCard>

            {/* Contact info */}
            <AnimCard delay={150}>
              <div className="space-y-6">
                <div
                  className="p-8 rounded-sm"
                  style={{ background: MARINE, border: "1px solid oklch(0.695 0.105 68 / 0.15)" }}
                >
                  <h3
                    className="text-xl font-semibold mb-6"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                  >
                    Coordonnées
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}>
                        <Mail size={15} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "oklch(0.50 0.01 243)" }}>Email</div>
                        <a href="mailto:contact@gsfarbitral.ai" className="text-sm" style={{ color: "oklch(0.82 0.01 243)", textDecoration: "none" }}>
                          contact@gsfarbitral.ai
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}>
                        <Globe size={15} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "oklch(0.50 0.01 243)" }}>Périmètre</div>
                        <span className="text-sm" style={{ color: "oklch(0.82 0.01 243)" }}>Espace OHADA — 17 États membres</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: "oklch(0.695 0.105 68 / 0.15)", color: GOLD }}>
                        <Globe size={15} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "oklch(0.50 0.01 243)" }}>Plateforme</div>
                        <a href="https://gsfarbitral.ai" target="_blank" rel="noopener noreferrer" className="text-sm" style={{ color: GOLD_LIGHT, textDecoration: "none" }}>
                          gsfarbitral.ai
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="p-6 rounded-sm"
                  style={{ background: "oklch(0.695 0.105 68 / 0.07)", border: "1px solid oklch(0.695 0.105 68 / 0.22)" }}
                >
                  <h4
                    className="font-semibold text-base mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
                  >
                    EJI2
                  </h4>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: "oklch(0.45 0.01 243)" }}>
                    Pionnier de l'éthique numérique et de l'intelligence artificielle responsable, au service de la gouvernance technologique des institutions de demain.
                  </p>
                  <a
                    href="https://eji2.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium"
                    style={{ color: GOLD_MUTED, textDecoration: "none" }}
                  >
                    Visiter le site
                    <ArrowRight size={13} />
                  </a>
                </div>
              </div>
            </AnimCard>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
